import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons/faPlusCircle";
import { ORGANIZATIONS_REFERENCE_DATA_TYPES, OrganizationsReferenceData } from "@constants/reference-data";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ModalTypes } from "@enums/modal-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { getOrganizationsReduxRequest } from "@redux-requests/organizations";
import { useModalContext } from "@context/use-modal-context";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { ButtonWithIcon } from "../../common/button-with-icon/button-with-icon";
import { LoadingIndicator } from "../../common/loading-indicator/loading-indicator";
import { AddOrganizationModal } from "./add-organization-modal/add-organization-modal";
import { OrganizationsTableContainer } from "./organizations-table-container/organizations-table-container";
import { CardContainer } from "../../common/card-container/card-container";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import { getSessionState, getTableSettingsState } from "@redux/selectors/root-selectors";
import { getOrganizationsDataState } from "@redux/selectors/organizations-selectors";

export const OrganizationsContainer = () => {
    const modalContext = useModalContext();
    const dispatch = useDispatch();

    const { session } = useSelector(getSessionState);
    const { organizations: organizationsTableState } = useSelector(getTableSettingsState);
    const {
        isLoadingAxnCompanies,
        axnCompanies
    } = useSelector(getOrganizationsDataState);
    const referenceData = useReferenceDataSelector<OrganizationsReferenceData>(ORGANIZATIONS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadAxnCompanies, setShouldLoadAxnCompanies] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.relyingParties && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATIONS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.ORGANIZATIONS
            );
        }
    }, [referenceData.isLoading.relyingParties, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.auth0IdentityProviders &&
            referenceData.axnAdminUserPropertyBundles &&
            referenceData.customerSupportTiers
        );
    }, [
        referenceData.auth0IdentityProviders,
        referenceData.axnAdminUserPropertyBundles,
        referenceData.customerSupportTiers
    ]);

    useEffect(() => {
        if (isReferenceDataPresent && !isLoadingAxnCompanies && shouldLoadAxnCompanies) {
            setShouldLoadAxnCompanies(false);
            getOrganizationsReduxRequest(dispatch)();
        }
    }, [isReferenceDataPresent, isLoadingAxnCompanies, shouldLoadAxnCompanies]);

    const renderAddOrganizationModal = useMemo(() => (onModalClose: () => void) => {
        return (
            <AddOrganizationModal
                auth0IdentityProviders={referenceData.auth0IdentityProviders}
                axnAdminUserPropertyBundles={referenceData.axnAdminUserPropertyBundles}
                customerSupportTiers={referenceData.customerSupportTiers}
                onModalClose={onModalClose}
            />
        );
    }, [
        referenceData.auth0IdentityProviders,
        referenceData.axnAdminUserPropertyBundles,
        referenceData.customerSupportTiers
    ]);

    const isLoading = useMemo(() => {
        return isLoadingAxnCompanies || referenceData.isLoading.organizations;
    }, [
        referenceData.isLoading.organizations,
        isLoadingAxnCompanies
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            axnCompanies &&
            isReferenceDataPresent &&
            !shouldLoadAxnCompanies &&
            !shouldLoadReferenceData
        );
    }, [
        isLoading,
        axnCompanies,
        isReferenceDataPresent,
        shouldLoadAxnCompanies,
        shouldLoadReferenceData
    ]);

    const headerButton = useMemo(() => {
        if (session.userPermissions?.canCreateOrganizations) {
            const onClick = () => {
                modalContext.setModalState({
                    modalType: ModalTypes.AddOrganization,
                    renderFunction: renderAddOrganizationModal
                });
            };
            return (
                <ButtonWithIcon
                    id="add-new-organization"
                    title={resources.pages.organizations.headerButton}
                    text={resources.pages.organizations.headerButton}
                    onClick={onClick}
                    icon={faPlusCircle}
                    disabled={isLoading || !isLoaded}
                />
            );
        } else {
            return null;
        }
    }, [isLoading, isLoaded, session.userPermissions, renderAddOrganizationModal]);

    return (
        <CardContainer isPageHeight={true}>
            <ContentPageHeader title={resources.pages.organizations.header} headerButton={headerButton} />
            <ContentPageBody isMaxWidth={true} isPageHeight={true}>
                {isLoaded && (
                    <OrganizationsTableContainer
                        axnCompanies={axnCompanies}
                        tableState={organizationsTableState}
                        isPageHeight={true}
                        customerSupportTiers={referenceData.customerSupportTiers}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};